class PremiumPressSocial extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.viewport = this.querySelector('[data-pps-viewport]');
    this.track = this.querySelector('[data-pps-track]');
    if (!this.track || !this.viewport) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.cards = Array.from(this.track.children);
    this.count = this.cards.length;
    this.index = 0;
    this.physical = 1;
    this.mobile = matchMedia('(max-width: 749px)');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.dataset.autoplay !== 'true';
    this.inView = false;
    this.focused = false;
    this.hovered = false;
    this.editorSelected = false;
    this.busy = false;
    this.pointer = null;
    this.clones = [];

    if (this.count > 1) {
      const clone = (card) => {
        const copy = card.cloneNode(true);
        copy.dataset.ppsClone = '';
        copy.setAttribute('aria-hidden', 'true');
        copy.inert = true;
        for (const element of [copy, ...copy.querySelectorAll('*')]) {
          element.removeAttribute('id');
          for (const attr of [...element.attributes]) {
            if (attr.name.startsWith('data-shopify-')) element.removeAttribute(attr.name);
          }
        }
        return copy;
      };
      this.clones = [clone(this.cards.at(-1)), clone(this.cards[0])];
      this.track.prepend(this.clones[0]);
      this.track.append(this.clones[1]);
    }
    this.dataset.ready = 'true';

    this.addEventListener('click', (event) => {
      if (this.suppressClick && event.target.closest('[data-pps-card]')) {
        event.preventDefault();
        return;
      }
      if (event.target.closest('[data-pps-prev]')) this.move(-1, true);
      if (event.target.closest('[data-pps-next]')) this.move(1, true);
      const dot = event.target.closest('[data-pps-dot]');
      if (dot) this.goTo(Number(dot.dataset.ppsDot), true);
      if (event.target.closest('[data-pps-pause]')) {
        this.paused = !this.paused;
        this.focused = false;
        this.hovered = false;
        this.updatePause();
        this.schedule();
      }
    }, { signal });
    this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, { signal });
    this.addEventListener('focusout', (event) => { this.focused = this.contains(event.relatedTarget); this.schedule(); }, { signal });
    this.viewport.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
    }, { signal });
    this.viewport.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, { signal });
    this.viewport.addEventListener('keydown', (event) => {
      if (!this.mobile.matches || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      event.preventDefault();
      this.move(event.key === 'ArrowRight' ? 1 : -1, true);
    }, { signal });
    this.viewport.addEventListener('pointerdown', (event) => {
      if (!this.mobile.matches || !event.isPrimary || event.button !== 0) return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      this.suppressClick = false;
      this.schedule();
    }, { signal, passive: true });
    window.addEventListener('pointerup', (event) => {
      if (this.pointer?.id !== event.pointerId) return;
      const dx = event.clientX - this.pointer.x;
      const dy = event.clientY - this.pointer.y;
      this.pointer = null;
      if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        this.suppressClick = true;
        clearTimeout(this.clickTimer);
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 400);
        this.move(dx < 0 ? 1 : -1, true);
      }
      this.schedule();
    }, { signal, passive: true });
    window.addEventListener('pointercancel', () => { this.pointer = null; this.schedule(); }, { signal });
    this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.track.addEventListener('transitionend', (event) => {
      if (event.target === this.track && event.propertyName === 'transform') this.finish();
    }, { signal });
    this.mobile.addEventListener('change', () => this.layout(), { signal });
    this.motion.addEventListener('change', () => { this.finish(); this.updatePause(); this.schedule(); }, { signal });
    document.addEventListener('visibilitychange', () => this.schedule(), { signal });
    document.addEventListener('shopify:block:select', (event) => {
      const card = event.target.closest?.('[data-pps-card]');
      if (!card || !this.cards.includes(card)) return;
      this.editorSelected = true;
      this.finish();
      this.goTo(this.cards.indexOf(card), false);
      this.schedule();
    }, { signal });
    document.addEventListener('shopify:block:deselect', (event) => {
      if (!this.contains(event.target)) return;
      this.editorSelected = false;
      this.schedule();
    }, { signal });
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.viewport);
    } else {
      window.addEventListener('resize', () => this.layout(), { signal });
    }
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (this.inView) this.dataset.inView = 'true';
        this.schedule();
      }, { threshold: 0.15 });
      this.observer.observe(this.viewport);
    } else {
      this.inView = true;
      this.dataset.inView = 'true';
    }
    this.layout();
  }

  layout() {
    clearTimeout(this.finishTimer);
    this.busy = false;
    // Clear the native fallback's snap offset before positioning the track.
    this.viewport.scrollLeft = 0;
    this.step = this.cards[0]?.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).gap) || 18);
    this.physical = this.index + 1;
    this.querySelector('[data-pps-controls]').hidden = !this.mobile.matches || this.count < 2;
    this.viewport.setAttribute('aria-roledescription', this.mobile.matches ? 'carousel' : 'article grid');
    this.jump();
    this.updateCards();
    this.updatePause();
    this.schedule();
  }

  jump() {
    this.track.style.transition = 'none';
    this.track.style.transform = this.mobile.matches && this.count > 1 ? `translate3d(${-this.physical * this.step}px,0,0)` : 'none';
    void this.track.offsetWidth;
    this.track.style.removeProperty('transition');
  }

  move(direction, manual = false) {
    if (this.busy || !this.mobile.matches || this.count < 2) return;
    this.physical += direction;
    this.index = (this.index + direction + this.count) % this.count;
    this.animate(manual);
  }

  goTo(index, manual) {
    if (this.busy || !this.mobile.matches || index === this.index || index < 0 || index >= this.count) return;
    this.index = index;
    this.physical = index + 1;
    this.animate(manual);
  }

  animate(manual) {
    this.busy = true;
    if (this.viewport.contains(document.activeElement)) this.querySelector('[data-pps-next]').focus({ preventScroll: true });
    this.track.style.transform = `translate3d(${-this.physical * this.step}px,0,0)`;
    this.updateCards();
    if (manual) {
      this.querySelector('[data-pps-status]').textContent = `Article ${this.index + 1} of ${this.count}`;
      this.schedule();
    }
    if (this.motion.matches) this.finish();
    else this.finishTimer = setTimeout(() => this.finish(), 650);
  }

  finish() {
    clearTimeout(this.finishTimer);
    if (!this.busy) return;
    this.physical = this.index + 1;
    this.jump();
    this.busy = false;
  }

  updateCards() {
    this.cards.forEach((card, index) => {
      const hidden = this.mobile.matches && index !== this.index;
      card.inert = hidden;
      if (hidden) card.setAttribute('aria-hidden', 'true');
      else card.removeAttribute('aria-hidden');
    });
    this.querySelectorAll('[data-pps-dot]').forEach((dot, index) => {
      if (index === this.index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  updatePause() {
    const stopped = this.paused || this.motion.matches;
    const button = this.querySelector('[data-pps-pause]');
    button.hidden = this.motion.matches;
    button.setAttribute('aria-label', stopped ? 'Start automatic article rotation' : 'Pause automatic article rotation');
    this.querySelector('[data-pps-pause-icon]').textContent = stopped ? '▷' : 'Ⅱ';
  }

  schedule() {
    clearInterval(this.timer);
    if (!this.mobile.matches || this.count < 2 || this.paused || this.motion.matches || !this.inView || this.hovered || this.focused || this.pointer || this.editorSelected || document.hidden) return;
    this.timer = setInterval(() => this.move(1), 2000);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    clearInterval(this.timer);
    clearTimeout(this.finishTimer);
    clearTimeout(this.clickTimer);
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.clones?.forEach((clone) => clone.remove());
    this.cards?.forEach((card) => { card.inert = false; card.removeAttribute('aria-hidden'); });
    this.track?.style.removeProperty('transform');
    delete this.dataset.ready;
  }
}

if (!customElements.get('premium-press-social')) customElements.define('premium-press-social', PremiumPressSocial);
